import { FC, memo, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import classNames from 'classnames';
import { isNil } from 'lodash';
// Import Components
import { BarChart, Icon, Tooltip } from '@geneui/components';
import SectionWithTitle from 'components/common/SectionWithTitle';
// Import Enums
import { MetricTypeEnum } from 'enums';
// Import Types
import { ABTestChartContainerProps } from './types';
// Import Constants
import { DangerConfidencePercentage, l } from 'constants/common';

const ABTestChartContainer: FC<ABTestChartContainerProps> = ({
    titleKey,
    pilotGroup,
    controlGroup,
    impactPercentage,
    confidencePercentage,
    type,
    currencyCode,
}) => {
    const { t } = useTranslation();

    const isDangerZone = confidencePercentage !== null && confidencePercentage < DangerConfidencePercentage;
    const impact = isNil(impactPercentage) ? t(l.NA) : `${impactPercentage}%`;
    const confidence = isNil(confidencePercentage) ? t(l.NA) : `${confidencePercentage}%`;

    const ConfidencePercentageTooltipContent = useMemo(() => {
        return `${t(l.ConfidencePercentageDescription)}\n>=95% ${t(l.HighImpactDetected)}\n<95% ${t(
            l.NoImpactDetected,
        )}`;
    }, [t]);

    return (
        <SectionWithTitle
            title={
                <>
                    {t(l[`TestMetric${titleKey}`])}
                    {type === MetricTypeEnum.Money && ` (${currencyCode})`}
                    <Tooltip text={t(l[`TestMetric${titleKey}Description`])} position="bottom">
                        <Icon type="bc-icon-info" />
                    </Tooltip>
                </>
            }
            hasContentPadding={false}
        >
            <div className="ab-test-chart-container">
                <BarChart
                    chart={{
                        type: 'column',
                    }}
                    series={[
                        { name: t(l.PilotGroup), color: '#BB3F3A', data: [pilotGroup] },
                        { name: t(l.ControlGroup), color: '#1473E6', data: [controlGroup] },
                    ]}
                    categories={['']}
                    {...(type === MetricTypeEnum.Percentage
                        ? {
                              yAxis: [
                                  {
                                      labels: {
                                          format: '{value}%',
                                          style: {
                                              fontSize: '1.4rem',
                                              fontWeight: 600,
                                              color: 'rgb(60, 64, 67)',
                                          },
                                      },
                                      title: {
                                          text: '',
                                      },
                                  },
                              ],
                          }
                        : {})}
                />
                <div className="percentage-info-section">
                    <div>
                        <Tooltip text={t(l.ImpactPercentageDescription)}>
                            <div className="percentage-info-label-tooltip-container">
                                <p className="g-details-title">{t(l.ImpactPercentage)}</p>
                                <Icon type="bc-icon-info" />
                            </div>
                        </Tooltip>
                        <p
                            className={classNames('g-details-subtitle', {
                                'percentage-danger-zone': !isNil(impactPercentage) && isDangerZone,
                                'percentage-normal-zone': !isNil(impactPercentage) && !isDangerZone,
                            })}
                        >
                            {impact}
                        </p>
                    </div>
                    <div>
                        <Tooltip text={ConfidencePercentageTooltipContent} style={{ whiteSpace: 'pre-line' }}>
                            <div className="percentage-info-label-tooltip-container">
                                <p className="g-details-title">{t(l.ConfidencePercentage)}</p>
                                <Icon type="bc-icon-info" />
                            </div>
                        </Tooltip>
                        <p
                            className={classNames('g-details-subtitle', {
                                'percentage-danger-zone': !isNil(confidencePercentage) && isDangerZone,
                                'percentage-normal-zone': !isNil(confidencePercentage) && !isDangerZone,
                            })}
                        >
                            {confidence}
                        </p>
                    </div>
                </div>
            </div>
        </SectionWithTitle>
    );
};

export default memo(ABTestChartContainer);
